import { useEffect, useState } from 'react';
import api from '../../api/client';
import { useFincaId } from '../../context/FincaContext';
import PiscinaBuscador from '../../components/PiscinaBuscador';
import { format } from 'date-fns';

const KG_POR_SACO = 25;
const MODOS = ['Cronograma', 'Automático', 'Híbrido'];

const hoyISO = () => new Date().toISOString().split('T')[0];

function fmtKg(v) {
  const n = Number(v);
  if (v === null || v === undefined || v === '' || isNaN(n)) return '-';
  return n.toFixed(2);
}
function fmtSacos(v) {
  const n = Number(v);
  if (v === null || v === undefined || v === '' || isNaN(n)) return '-';
  return (n / KG_POR_SACO).toFixed(2);
}
function fmtFecha(f) {
  if (!f) return '-';
  try { return format(new Date(f), 'dd/MM/yyyy'); } catch { return f; }
}
function fmtHora(h) {
  if (!h) return '-';
  return String(h).slice(0, 5);
}

export default function MonitoreoRegistroBiologo() {
  const fincaIdContext = useFincaId();

  const [sectores, setSectores] = useState([]);
  const [piscinas, setPiscinas] = useState([]);
  const [tiposBalanceado, setTiposBalanceado] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const [fecha, setFecha] = useState(hoyISO());
  const [sectorId, setSectorId] = useState('');
  const [piscinaId, setPiscinaId] = useState('');
  const [tipoBalanceado, setTipoBalanceado] = useState('');
  const [modo, setModo] = useState('');

  const [mostrarSacos, setMostrarSacos] = useState(() => {
    const stored = localStorage.getItem('monitoreo_mostrarSacos');
    return stored === null ? true : stored === 'true';
  });
  const [mostrarRegistradoPor, setMostrarRegistradoPor] = useState(() => {
    const stored = localStorage.getItem('monitoreo_mostrarRegistradoPor');
    return stored === null ? true : stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem('monitoreo_mostrarSacos', mostrarSacos);
  }, [mostrarSacos]);

  useEffect(() => {
    localStorage.setItem('monitoreo_mostrarRegistradoPor', mostrarRegistradoPor);
  }, [mostrarRegistradoPor]);

  useEffect(() => {
    if (fincaIdContext) cargarSectores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaIdContext]);

  useEffect(() => {
    cargarTiposBalanceado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sectorId) cargarPiscinas(sectorId);
    else setPiscinas([]);
    setPiscinaId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorId]);

  useEffect(() => {
    if (fincaIdContext && fecha) cargarHistorial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha, sectorId, piscinaId, tipoBalanceado, modo, fincaIdContext]);

  const cambiarDia = (delta) => {
    const [a, m, d] = fecha.split('-').map(Number);
    const dt = new Date(a, m - 1, d);
    dt.setDate(dt.getDate() + delta);
    setFecha(dt.toISOString().split('T')[0]);
  };

  const irHoy = () => setFecha(hoyISO());

  const cargarSectores = async () => {
    try {
      const { data } = await api.get(`/sectores/finca/${fincaIdContext}`);
      setSectores(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const cargarPiscinas = async (id) => {
    try {
      const { data } = await api.get(`/piscinas/sector/${id}?incluirInactivas=true`);
      setPiscinas(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const cargarTiposBalanceado = async () => {
    try {
      const { data } = await api.get('/tipos-balanceado');
      const arr = Array.isArray(data) ? data : [];
      setTiposBalanceado(arr.filter((t) => t.activo !== false));
    } catch (err) {
      console.error(err);
    }
  };

  const cargarHistorial = async () => {
    setCargando(true);
    setError(null);
    try {
      const params = { desde: fecha, hasta: fecha };
      if (sectorId) params.sector_id = sectorId;
      if (piscinaId) params.piscina_id = piscinaId;
      if (tipoBalanceado) params.tipo_balanceado = tipoBalanceado;
      if (modo) params.modo = modo;

      const { data } = await api.get('/registro-biologo/historial', { params });
      setRegistros(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Error al cargar historial');
    } finally {
      setCargando(false);
    }
  };

  const limpiarFiltros = () => {
    setFecha(hoyISO());
    setSectorId('');
    setPiscinaId('');
    setTipoBalanceado('');
    setModo('');
  };

  const anchoTabla = (() => {
    let w = 1400;
    if (mostrarSacos) w += 300;
    if (!mostrarRegistradoPor) w -= 150;
    return `${w}px`;
  })();

  return (
    <div className="p-6">
      {/* Barra de filtros compacta (una sola fila en desktop) */}
      <div className="bg-white rounded shadow mb-4 px-3 py-2 flex flex-wrap items-end gap-x-2 gap-y-2">
        {/* Fecha con flechas */}
        <div className="flex flex-col">
          <span className="text-[10px] font-medium text-gray-500 mb-0.5">Fecha</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => cambiarDia(-1)}
              className="px-2 py-1.5 bg-primary text-white rounded hover:bg-blue-800 text-xs font-bold"
              title="Día anterior"
            >
              ←
            </button>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="px-2 py-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-xs w-[130px]"
            />
            <button
              type="button"
              onClick={() => cambiarDia(1)}
              className="px-2 py-1.5 bg-primary text-white rounded hover:bg-blue-800 text-xs font-bold"
              title="Día siguiente"
            >
              →
            </button>
            <button
              type="button"
              onClick={irHoy}
              className="px-2 py-1.5 border border-primary text-primary rounded hover:bg-blue-50 text-xs font-medium"
            >
              Hoy
            </button>
          </div>
        </div>

        <label className="flex flex-col">
          <span className="text-[10px] font-medium text-gray-500 mb-0.5">Sector</span>
          <select
            value={sectorId}
            onChange={(e) => setSectorId(e.target.value)}
            className="px-2 py-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-xs w-[120px]"
          >
            <option value="">Todos</option>
            {sectores.map((s) => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </label>

        <div className="flex flex-col w-[140px]">
          <span className="text-[18px] font-medium text-gray-500 mb-0.5">Filtro Piscinas</span>
          <PiscinaBuscador
            piscinas={piscinas}
            value={piscinaId}
            onChange={(id) => setPiscinaId(id)}
            disabled={!sectorId}
            permitirTodas={true}
            valorTodas=""
            labelTodas="Todas"
          />
        </div>

        <label className="flex flex-col">
          <span className="text-[10px] font-medium text-gray-500 mb-0.5">Tipo balanceado</span>
          <select
            value={tipoBalanceado}
            onChange={(e) => setTipoBalanceado(e.target.value)}
            className="px-2 py-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-xs w-[140px]"
          >
            <option value="">Todos</option>
            {tiposBalanceado.map((t) => (
              <option key={t.id} value={t.nombre}>{t.nombre}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col">
          <span className="text-[10px] font-medium text-gray-500 mb-0.5">Modo</span>
          <select
            value={modo}
            onChange={(e) => setModo(e.target.value)}
            className="px-2 py-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-xs w-[120px]"
          >
            <option value="">Todos</option>
            {MODOS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>

        <button
          onClick={limpiarFiltros}
          className="px-2 py-1.5 border rounded text-xs hover:bg-gray-100 self-end"
          title="Limpiar filtros"
        >
          ✕ Limpiar
        </button>

         {/* Separador visual antes de los toggles de columnas */}
        <div className="hidden lg:block self-stretch w-px bg-gray-200 mx-1 my-1" />

        {/* Toggles de columnas — título arriba, checkboxes centrados abajo */}
        <div className="flex flex-col items-center justify-center self-stretch">
          <span className="text-[20px] font-medium text-gray-500 mb-1 text-center">
            Mostrar...
          </span>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <label className="flex items-center gap-1.5 px-2 py-1.5 border rounded cursor-pointer hover:bg-gray-50 select-none">
              <input
                type="checkbox"
                checked={mostrarSacos}
                onChange={(e) => setMostrarSacos(e.target.checked)}
                className="w-3.5 h-3.5 cursor-pointer accent-primary"
              />
              <span className="text-xl text-gray-700 whitespace-nowrap">Sacos</span>
            </label>

            <label className="flex items-center gap-1.5 px-2 py-1.5 border rounded cursor-pointer hover:bg-gray-50 select-none">
              <input
                type="checkbox"
                checked={mostrarRegistradoPor}
                onChange={(e) => setMostrarRegistradoPor(e.target.checked)}
                className="w-3.5 h-3.5 cursor-pointer accent-primary"
              />
              <span className="text-xl text-gray-700 whitespace-nowrap">Registrado por</span>
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 rounded">{error}</div>
      )}

      {cargando && (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          Cargando...
        </div>
      )}

      {!cargando && !error && registros.length === 0 && (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          No hay registros biólogo para esa fecha con esos filtros.
        </div>
      )}

      {!cargando && registros.length > 0 && (
        <div className="bg-white rounded shadow overflow-hidden">
          <div className="p-3 border-b bg-gray-50 text-sm text-gray-700">
            <strong>{registros.length}</strong>{' '}
            registro{registros.length !== 1 ? 's' : ''} para{' '}
            <strong>{fmtFecha(fecha)}</strong>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" style={{ minWidth: anchoTabla }}>
              <thead className="bg-gray-100 border-b-2 border-gray-300">
                <tr>
                  <Th centro>Fecha</Th>
                  <Th>Sector</Th>
                  <Th>Piscina</Th>
                  <Th centro>Has</Th>
                  <Th colSpan={mostrarSacos ? 2 : 1} centro>Sobrante tolva</Th>
                  <Th colSpan={mostrarSacos ? 2 : 1} centro>Abastecido</Th>
                  <Th colSpan={mostrarSacos ? 2 : 1} centro>Total día</Th>
                  <Th colSpan={mostrarSacos ? 2 : 1} centro>Dieta proyectada</Th>
                  <Th>Tipo balanceado</Th>
                  <Th centro>Hora inicio</Th>
                  <Th centro>Hora fin</Th>
                  <Th centro>Modo</Th>
                  <Th centro>Nivel</Th>
                  {mostrarRegistradoPor && <Th>Registrado por</Th>}
                  <Th>Observaciones</Th>
                </tr>
                <tr>
                  <Th /><Th /><Th /><Th />
                  <Th centro>Kg</Th>{mostrarSacos && <Th centro>Sacos</Th>}
                  <Th centro>Kg</Th>{mostrarSacos && <Th centro>Sacos</Th>}
                  <Th centro>Kg</Th>{mostrarSacos && <Th centro>Sacos</Th>}
                  <Th centro>Kg</Th>{mostrarSacos && <Th centro>Sacos</Th>}
                  <Th /><Th /><Th /><Th /><Th />
                  {mostrarRegistradoPor && <Th />}
                  <Th />
                </tr>
              </thead>
              <tbody>
                {registros.map((r) => (
                  <tr key={r.id} className="border-b hover:bg-blue-50">
                    <Td centro>{fmtFecha(r.fecha)}</Td>
                    <Td>{r.sector_nombre}</Td>
                    <Td>{r.piscina_codigo}</Td>
                    <Td centro>{r.area_ha ?? '-'}</Td>
                    <Td centro>{fmtKg(r.sobrante_tolva_kg)}</Td>
                    {mostrarSacos && <Td centro>{fmtSacos(r.sobrante_tolva_kg)}</Td>}
                    <Td centro>{fmtKg(r.alimento_abastecido_kg)}</Td>
                    {mostrarSacos && <Td centro>{fmtSacos(r.alimento_abastecido_kg)}</Td>}
                    <Td centro>{fmtKg(r.cantidad_total_kg)}</Td>
                    {mostrarSacos && <Td centro>{fmtSacos(r.cantidad_total_kg)}</Td>}
                    <Td centro>{fmtKg(r.dieta_proyectada_kg)}</Td>
                    {mostrarSacos && <Td centro>{fmtSacos(r.dieta_proyectada_kg)}</Td>}
                    <Td>{r.tipo_balanceado || '-'}</Td>
                    <Td centro>{fmtHora(r.hora_inicio)}</Td>
                    <Td centro>{fmtHora(r.hora_fin)}</Td>
                    <Td centro>{r.modo || '-'}</Td>
                    <Td centro>{r.nivel || '-'}</Td>
                    {mostrarRegistradoPor && <Td>{r.usuario_nombre || '-'}</Td>}
                    <Td>{r.observaciones || '-'}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Th({ children, colSpan, centro }) {
  return (
    <th
      colSpan={colSpan}
      className={`px-2 py-2 font-semibold border-r border-gray-200 whitespace-nowrap ${centro ? 'text-center' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function Td({ children, centro }) {
  return (
    <td className={`px-2 py-1 border-r border-gray-200 whitespace-nowrap ${centro ? 'text-center' : ''}`}>
      {children}
    </td>
  );
}